/**
 * Acto médico del episodio (2026-08-25, dirección): el médico registra su
 * tratamiento o cirugía DESDE el evolutivo, ligado al episodio y con su
 * fecha — así lleva su propio registro de actos médicos sin salir de la
 * historia, y facturación lo ve en Colaboradores.
 *
 * «SOLO VALORACIÓN» (administración, 5/10/2026): «impide crear episodios para
 * pacientes que solo han venido a valoración y no se les ha realizado ningún
 * tratamiento». Se elige EN LUGAR de un tratamiento: 0 €, sin IVA que marcar,
 * y el episodio se cierra sin más. Es una marca del EPISODIO, no un elemento
 * del tarifario ni un acto de colaborador: no se factura ni se reparte, así
 * que Colaboradores no la ve. La pone el editor (`onSoloValoracion`), que es
 * quien sabe guardar antes lo tecleado. No se le ofrece a quien está exento
 * de marcar el IVA (dirección): sigue como estaba.
 *
 * GESTIÓN (revisión del 7/10/2026): ve el botón «Cirugía» en cualquier
 * episodio, pero el diálogo se abría como si fuera el médico (sin elegir
 * médico) y el servidor contestaba 422 «Elige el médico…». Ahora se abre como
 * gestión, con el desplegable de los médicos colaboradores, y si el autor del
 * episodio es uno de ellos, ya viene elegido.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import ColabActividadDialog from '../ColabActividadDialog';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');

const ROLES_GESTION = ['admin', 'direccion', 'administrativo'];

export default function ActoMedicoEpisodio({
  episodio, patient, onSoloValoracion = null, marcandoSoloValoracion = false, onActos = null,
}) {
  const { user } = useAuth();
  const rol = (user?.role || '').toLowerCase();
  const esGestion = ROLES_GESTION.includes(rol);
  const puedeVer = esGestion || rol === 'doctor';
  const soloValoracion = !!episodio?.solo_valoracion;
  const [actos, setActos] = useState(null);
  const [esDiaQx, setEsDiaQx] = useState(false);
  const [catalogos, setCatalogos] = useState(null);
  const [dialogo, setDialogo] = useState(null);   // 'tratamiento' | 'cirugia'
  // Gestión: los médicos colaboradores posibles (null = aún sin pedir).
  const [medicos, setMedicos] = useState(null);

  const cargar = useCallback(() => {
    if (!puedeVer || !episodio?.id) return;
    api.get('/api/colaboradores/actividades', { params: { episodio_id: episodio.id } })
      .then((r) => { setActos(r.data?.items || []); setEsDiaQx(!!r.data?.es_dia_quirofano); })
      .catch(() => setActos([]));
  }, [puedeVer, episodio?.id]);

  useEffect(() => { cargar(); }, [cargar]);

  // El editor necesita saber si el episodio lleva un acto que se factura: con
  // la ficha sin el IVA decidido, es lo que hace que la pregunta del IVA salga
  // también en una visita que no es la primera (Antonio, 5/10/2026).
  useEffect(() => { if (actos) onActos?.(actos); }, [actos, onActos]);

  if (!puedeVer) return null;

  const abrir = async (tipo) => {
    if (!catalogos) {
      api.get('/api/colaboradores/catalogos').then((r) => setCatalogos(r.data)).catch(() => {});
    }
    if (esGestion && medicos === null) {
      // Antes de abrir: así el médico del episodio viene ya elegido y no se
      // reinicia el formulario cuando llega la lista.
      const lista = await api.get('/api/colaboradores/medicos-posibles')
        .then((r) => (Array.isArray(r.data?.medicos) ? r.data.medicos : []))
        .catch(() => []);
      setMedicos(lista);
    }
    setDialogo(tipo);
  };
  // El autor del episodio, si es uno de los médicos colaboradores.
  const autor = episodio?.doctor_user_id;
  const medicoDelEpisodio = esGestion && autor && (medicos || []).some((m) => m.id === autor)
    ? { medico_id: autor, medico_nombre: episodio?.doctor_name || '' } : null;

  const nombrePaciente = [patient?.name, patient?.surname1, patient?.surname2]
    .filter(Boolean).join(' ').trim();

  // Solo con el episodio sin actos que se facturen: «Solo valoración» va EN
  // LUGAR de un tratamiento, y el servidor no deja que convivan (con la marca
  // no se pide el IVA, y se lo saltaría a un tratamiento de verdad).
  const facturables = (actos || []).filter((a) => !a.revision_sin_tratamiento);
  const ofrecerSoloValoracion = !!onSoloValoracion && !user?.exento_marca_iva
    && !soloValoracion && actos !== null && facturables.length === 0;

  return (
    <div className="mt-3 border-t border-dashed border-border pt-2.5" data-testid="acto-medico-episodio">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Banknote className="w-3.5 h-3.5 text-[#2E6E5E]" /> Acto médico (facturación)
        </span>
        {!soloValoracion && (
          <button type="button" onClick={() => abrir('tratamiento')}
            className="inline-flex items-center gap-1 text-xs rounded-md border border-[#2E6E5E]/40 text-[#1E4A3F] px-2 py-1 hover:bg-[rgba(46,110,94,0.06)]"
            data-testid="acto-medico-tratamiento">
            <Plus className="w-3 h-3" /> Tratamiento
          </button>
        )}
        {!soloValoracion && (esGestion || (user?.colab_cirugias && esDiaQx)) && (
          <button type="button" onClick={() => abrir('cirugia')}
            className="inline-flex items-center gap-1 text-xs rounded-md border border-border text-muted-foreground px-2 py-1 hover:border-[#2E6E5E]/40 hover:text-[#1E4A3F]"
            data-testid="acto-medico-cirugia">
            <Plus className="w-3 h-3" /> Cirugía
          </button>
        )}
        {ofrecerSoloValoracion && (
          <button type="button" onClick={() => onSoloValoracion(true)} disabled={marcandoSoloValoracion}
            className="inline-flex items-center gap-1 text-xs rounded-md border border-dashed border-[#2E6E5E]/60 text-[#1E4A3F] px-2 py-1 hover:bg-[rgba(46,110,94,0.06)] disabled:opacity-60"
            title="No se ha hecho ningún tratamiento: 0,00 €, sin IVA que marcar"
            data-testid="acto-medico-solo-valoracion">
            Solo valoración
          </button>
        )}
      </div>
      {soloValoracion && (
        <div className="mt-1.5" data-testid="acto-medico-solo-valoracion-marcada">
          <div className="text-xs text-[#13231E] flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2E6E5E]" />
            <span className="min-w-0 truncate">Solo valoración, sin tratamiento</span>
            <span className="ml-auto tabular-nums font-medium shrink-0">{eur(0)}</span>
            {onSoloValoracion && (
              <button type="button" onClick={() => onSoloValoracion(false)} disabled={marcandoSoloValoracion}
                className="shrink-0 text-[11px] text-muted-foreground underline hover:text-[#1E4A3F] disabled:opacity-60"
                title="Se pulsó por error, o al final sí hubo tratamiento"
                data-testid="acto-medico-quitar-solo-valoracion">
                Quitar
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            No se factura ni entra en el reparto, y no hay IVA que marcar.
          </p>
        </div>
      )}
      {!soloValoracion && actos && actos.length === 0 && (
        <p className="mt-1 text-[11px] text-amber-700">
          {ofrecerSoloValoracion
            // El servidor no exige el acto para cerrar desde el 27/8 (dirección:
            // «déjalo solo como opcional»), pero este cartel seguía diciendo
            // «Obligatorio» y era lo que los médicos leían (5/10/2026).
            ? 'Registra el tratamiento, o pulsa «Solo valoración» si no se ha hecho ninguno.'
            : 'Obligatorio antes de cerrar: registra el acto o marca «revisión sin tratamiento».'}
        </p>
      )}
      {actos && actos.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {actos.map((a) => (
            <li key={a.id} className="text-xs text-[#13231E] flex items-center gap-2" data-testid={`acto-${a.id}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.pagado ? 'bg-[#2E6E5E]' : 'bg-amber-500'}`} />
              <span className="min-w-0 truncate">
                {a.tipo === 'tratamiento'
                  ? `${a.tratamiento}${a.productos?.length ? ` · ${a.productos.map((l) => `${l.producto}${l.cantidad > 1 ? ` ×${l.cantidad}` : ''}`).join(' + ')}` : a.producto ? ` · ${a.producto}${a.cantidad > 1 ? ` ×${a.cantidad}` : ''}` : ''}`
                  : `${a.intervencion} (cirugía${a.lugar_nombre ? ` · ${a.lugar_nombre}` : ''})`}
              </span>
              <span className="ml-auto tabular-nums font-medium shrink-0">{eur(a.parte_medico)}</span>
            </li>
          ))}
        </ul>
      )}
      <ColabActividadDialog
        tipo={dialogo}
        onClose={() => setDialogo(null)}
        catalogos={catalogos}
        medicos={esGestion ? (medicos || []) : []}
        esGestion={esGestion}
        medicoActual={medicoDelEpisodio}
        fijo={{
          paciente_nombre: nombrePaciente || patient?.name || '',
          patient_id: patient?.id,
          fecha: (episodio?.date || episodio?.created_at || '').slice(0, 10),
          episodio_id: episodio?.id,
        }}
        // «Solo valoración» también como opción del desplegable de
        // tratamientos (5/10/2026): es donde la busca quien abre «Tratamiento».
        onSoloValoracion={ofrecerSoloValoracion ? () => onSoloValoracion(true) : null}
        onCreada={() => { setDialogo(null); cargar(); }}
      />
    </div>
  );
}
